import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { observer } from "mobx-react-lite";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "../../../components/index";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Switch,
} from "@mui/material";
import { Card, CardContent, Stack } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { DataGrid } from "@mui/x-data-grid";
import { SuittService } from "../../../services";
import { Link, useNavigate } from "react-router-dom";
import { snackActions } from "../../../utils/SnackBarUtils";
import { StoreContext } from "../../../App";
import UsersFilter from "./components/UsersFilter";
import { Page, ConfirmDialog } from "../../../components";

const FileDownload = require("js-file-download");

const Users = observer(() => {
  const suittService = useMemo(() => new SuittService(), []);
  const navigate = useNavigate();
  const { auth } = useContext(StoreContext);
  const user = auth.user;
  const roleLabel = (role) =>
    ({
      HR: "HR",
      Teacher: "Викладач",
      ViceRectorForScientificWork: "Проректор з наукової роботи",
      ViceRectorForEducationalWork: "Проректор з навчальної роботи",
      DeanOfTheFaculty: "Декан факультету",
      HeadOfTheDepartment: "Завідувач кафедри",
    }[role] ||
    role ||
    "-");

  const commonCol = { align: "center", headerAlign: "center" };

  const columns = [
    {
      field: "lastName",
      headerName: "Прізвище",
      flex: 1.1,
      minWidth: 140,
      valueGetter: (params) => params.row.fullNameUA.lastName,
      ...commonCol,
    },
    {
      field: "firstName",
      headerName: "Ім'я",
      flex: 1.0,
      minWidth: 120,
      valueGetter: (params) => params.row.fullNameUA.firstName,
      ...commonCol,
    },
    {
      field: "middleName",
      headerName: "По батькові",
      flex: 1.1,
      minWidth: 170,
      hide: true,
      valueGetter: (params) => params.row.fullNameUA.middleName,
      ...commonCol,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 2,
      minWidth: 240,
      ...commonCol,
    },
    {
      field: "orgUnit",
      headerName: "Підрозділ",
      flex: 1.2,
      minWidth: 140,
      valueGetter: (params) => params?.row?.orgUnit?.nameUk || "-",
      ...commonCol,
    },
    {
      field: "accountType",
      headerName: "Тип профілю",
      flex: 1.1,
      minWidth: 150,
      valueGetter: (params) => roleLabel(params.row.accountType),
      ...commonCol,
    },
    {
      field: "createdUpdated",
      headerName: "Створено / Оновлено",
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => {
        const created = params.row.createdAt
          ? new Date(params.row.createdAt).toLocaleDateString()
          : "-";
        const updated = params.row.updatedAt
          ? new Date(params.row.updatedAt).toLocaleDateString()
          : "-";
        const lastAct = params.row.lastActivity
          ? new Date(params.row.lastActivity).toLocaleDateString()
          : "-";
        return (
          <Box
            sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}
          >
            <Typography variant="body2">{`Ств.: ${created}`}</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
            >{`Оновл.: ${updated}`}</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
            >{`Актив.: ${lastAct}`}</Typography>
          </Box>
        );
      },
      ...commonCol,
    },
    {
      field: "status",
      headerName: "Статус",
      flex: 0.8,
      minWidth: 110,
      description: "",
      renderCell: (params) => {
        const rowId = params.row.id;
        const checked = params.row.status === "active";
        const toggleStatus = async (e) => {
          e.stopPropagation();
          const newStatus = e.target.checked ? "active" : "inactive";
          if (rowId === user._id)
            return snackActions.error("Неможливо змінити свій статус");
          const result = await suittService.changeUserStatus({
            userId: rowId,
            status: newStatus,
          });
          if (!result.status)
            return snackActions.error(
              "Щось пішло не так. Повторіть спробу пізніше"
            );
          setRefreshCounter((prev) => prev + 1);
          snackActions.success(
            newStatus === "active"
              ? "Користувача активовано"
              : "Користувача деактивовано"
          );
        };
        return (
          <Switch
            size="small"
            color="success"
            checked={checked}
            onChange={toggleStatus}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={rowId === user._id}
          />
        );
      },
      ...commonCol,
    },
    {
      field: "action",
      headerName: "Дія",
      width: 90,
      description: "",
      renderCell: (params) => {
        const userId = params.row.id;

        const downloadUserDataXlsx = async () => {
          const filename = `user_${params.row.fullNameUA.lastName}_${params.row.fullNameUA.firstName}.xlsx`;
          const result = await suittService.downloadUserDataXlsx(userId);

          if (result) {
            FileDownload(result, filename);
            return snackActions.success("Звіт завантажується");
          }
          snackActions.error("Дані за звітом не знайдені!");
        };

        const requestRemoveUser = () => {
          setConfirm({
            open: true,
            title: "Видалення користувача",
            content: "Ви впевнені, що хочете видалити користувача?",
            loading: false,
            onConfirm: async () => {
              setConfirm((prev) => ({ ...prev, loading: true }));
              const result = await suittService.removeUser(userId);
              setConfirm({
                open: false,
                title: "",
                content: "",
                loading: false,
                onConfirm: null,
              });
              if (!result.status)
                return snackActions.error(
                  "Щось пішло не так. Повторіть спробу пізніше"
                );
              setRefreshCounter((prev) => prev + 1);
              snackActions.success("Користувача видалено");
            },
          });
        };

        return (
          <Box
            sx={{ minWidth: 70 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <FormControl fullWidth variant="filled">
              <InputLabel id="label-action">Дія</InputLabel>
              <Select labelId="label-action" id="select-action" label={"Дія"}>
                <MenuItem>
                  <Button
                    component={Link}
                    to={`/cabinet/users/${params.row.id}`}
                    sx={{ width: "100%" }}
                  >
                    Перейти
                  </Button>
                </MenuItem>

                {user._id !== userId && (
                  <MenuItem>
                    <Button
                      onClick={downloadUserDataXlsx}
                      sx={{ width: "100%" }}
                    >
                      Завантажити xlsx
                    </Button>
                  </MenuItem>
                )}
                {user._id !== userId && (
                  <MenuItem>
                    <Button
                      component={Link}
                      to={`/cabinet/users/${params.row.id}/edit`}
                      sx={{ width: "100%" }}
                    >
                      Редагувати
                    </Button>
                  </MenuItem>
                )}
                {user._id !== userId && (
                  <MenuItem>
                    <Button onClick={requestRemoveUser} sx={{ width: "100%" }}>
                      Видалити
                    </Button>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        );
      },
    },
  ];
  const [users, setUsers] = useState({
    data: [],
    loading: true,
    filters: { status: "all", orgUnit: "all", accountType: "all", search: "" },
    total: 0,
    page: 0,
    pageSize: 25,
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [confirm, setConfirm] = useState({
    open: false,
    title: "",
    content: "",
    loading: false,
    onConfirm: null,
  });

  useEffect(() => {
    const controller = new AbortController();

    const loadUsers = async () => {
      const params = {
        status: users.filters.status,
        orgUnit: users.filters.orgUnit,
        search: users.filters.search,
        accountType: users.filters.accountType,
        page: users.page + 1,
        limit: users.pageSize,
        sortBy: "lastActivity",
        sortOrder: "desc",
      };

      try {
        const result = await suittService.getUsers(params, controller.signal);
        if (result.status) {
          const data = result.data.map((user) => ({ ...user, id: user._id }));
          setUsers((prev) => ({
            ...prev,
            data,
            loading: false,
            total: result.total || prev.total,
          }));
        } else {
          setUsers((prev) => ({ ...prev, loading: false }));
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          setUsers((prev) => ({ ...prev, loading: false }));
        }
      }
    };

    setUsers((prev) => ({ ...prev, loading: true }));
    loadUsers();

    return () => controller.abort();
  }, [
    users.page,
    users.pageSize,
    users.filters.status,
    users.filters.orgUnit,
    users.filters.search,
    users.filters.accountType,
    suittService,
    refreshCounter,
  ]);

  const updateFilters = useCallback(async (filters) => {
    setUsers((prev) => ({
      ...prev,
      filters: { ...prev.filters, ...filters },
      page: 0,
    }));
  }, []);

  const downloadUsersDataXlsx = async (e) => {
    const filename = "users_suitt.xlsx";
    const result = await suittService.downloadUsersDataXlsx();
    FileDownload(result, filename);
    return snackActions.success("Звіт завантажується");
  };

  const onRowClick = (item) => {
    navigate(`/cabinet/users/${item.id}`);
  };

  const actions = (
    <Button
      onClick={downloadUsersDataXlsx}
      variant={"contained"}
      startIcon={<DownloadIcon />}
    >
      Експорт даних (XLSX)
    </Button>
  );

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Page title="Повний список користувачів у системі" actions={actions}>
        <Grid item xs={12} sm={12} md={12} lg={12} sx={{ mt: 1 }}>
          <Card elevation={1}>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent={"space-between"}
              >
                <UsersFilter updateFilters={updateFilters} />
                <Box
                  sx={{ minWidth: 140, textAlign: { xs: "left", sm: "right" } }}
                >
                  <Typography variant="body2">{`Всього користувачів: ${users.total}`}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} sx={{ marginTop: 1 }}>
          <div style={{ height: "70vh", width: "100%" }}>
            <DataGrid
              rows={users.data}
              columns={columns}
              pagination
              paginationMode={"server"}
              rowCount={users.total}
              pageSizeOptions={[10, 25, 50]}
              paginationModel={{ page: users.page, pageSize: users.pageSize }}
              loading={users.loading}
              getRowHeight={() => "auto"}
              initialState={{
                columns: { columnVisibilityModel: { middleName: false } },
              }}
              onPaginationModelChange={(model) =>
                setUsers((prev) => ({
                  ...prev,
                  page: model.page,
                  pageSize: model.pageSize,
                }))
              }
              onRowClick={(event) => {
                onRowClick(event.row);
              }}
              disableRowSelectionOnClick
            />
          </div>
        </Grid>
        <ConfirmDialog
          open={confirm.open}
          title={confirm.title}
          content={confirm.content}
          loading={confirm.loading}
          onConfirm={confirm.onConfirm || (() => {})}
          onClose={() =>
            setConfirm({
              open: false,
              title: "",
              content: "",
              loading: false,
              onConfirm: null,
            })
          }
        />
      </Page>
    </ErrorBoundary>
  );
});

export default Users;
